import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useConsentApi } from '../../hooks/useConsentApi';
import {
  CONSENT_STATUS,
  selectConsentStatus,
  selectIsConsentGranted,
} from '../../state/aiSidekickConsentSlice';
import { expandDock } from '../../state/aiSidekickSlice';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import MaximizeGlyph from '../icons/MaximizeGlyph';
import SidekickDotsIcon from '../icons/SidekickDotsIcon';
import {
  DOCK_BAR_GLYPH_SIZE_PX,
  DOCK_BAR_LABEL_SKELETON_WIDTH_PX,
  DOCK_BRAND_ICON_SIZE_PX,
} from './constants';
import useStyles from './SidekickDock.styles';
import type { MinimizedBarProps } from './types';
import { useDockConsentEntry } from './useDockConsentEntry';

/**
 * Resting state of the docked entry point: a labelled bar pinned to
 * the host's bottom-right corner. The whole bar is the press target
 * so the hit area matches what the design reads as one control; the
 * trailing glyph is decorative rather than a nested button.
 *
 * The welcome card is a first-time surface, so a user who has already
 * accepted the disclaimer (this session or in the browser cache) goes
 * straight to the drawer instead.
 *
 * While that answer is on its way from the backend, a skeleton stands in
 * for the label and further presses are ignored, so a second click
 * cannot open the card underneath a drawer that is about to open.
 *
 * @param isPlanReady - Whether the account is known to hold a plan.
 */
function MinimizedBar({ isPlanReady }: MinimizedBarProps) {
  const { classes, cx } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const dispatch = useDispatch();
  const consentStatus = useSelector(selectConsentStatus);
  const isConsentGranted = useSelector(selectIsConsentGranted);
  const isConsentChecking = consentStatus === CONSENT_STATUS.CHECKING;
  const { handleBarClick } = useDockConsentEntry();
  const { hasBrowserConsent } = useConsentApi();
  // Until an entry settles the status, a press answers from the browser
  // when it can, so a stored acceptance already decides where it lands.
  const isConsentUnsettled =
    consentStatus === CONSENT_STATUS.IDLE ||
    consentStatus === CONSENT_STATUS.ERROR;
  const opensPanel =
    isPlanReady &&
    (isConsentGranted || (isConsentUnsettled && hasBrowserConsent()));

  const handleClick = useCallback(() => {
    if (isConsentChecking) {
      return;
    }
    if (!isPlanReady) {
      dispatch(expandDock());
      return;
    }
    handleBarClick();
  }, [dispatch, handleBarClick, isConsentChecking, isPlanReady]);

  return (
    <ButtonBase
      className={cx(classes.root, classes.bar)}
      onClick={handleClick}
      aria-label={
        opensPanel ? t('dock.openAriaLabel') : t('dock.expandAriaLabel')
      }
      // The drawer is a separate surface rather than this control
      // growing, so the bar only reports an expanded state when it is
      // the card it would reveal.
      aria-expanded={opensPanel ? undefined : false}
      aria-busy={isConsentChecking || undefined}
      data-testid="ai-sidekick-dock-bar"
    >
      <SidekickDotsIcon
        state="standby"
        decorative
        size={DOCK_BRAND_ICON_SIZE_PX}
      />
      {/* The skeleton sits inside the label so the line box, and with it
      the bar, keeps its height. */}
      <Typography className={classes.barLabel}>
        {isConsentChecking ? (
          <Skeleton
            variant="text"
            animation="pulse"
            width={DOCK_BAR_LABEL_SKELETON_WIDTH_PX}
            aria-hidden
            data-testid="ai-sidekick-dock-bar-skeleton"
          />
        ) : (
          t('panel.title')
        )}
      </Typography>
      <Box className={classes.barGlyph} component="span" aria-hidden="true">
        <MaximizeGlyph size={DOCK_BAR_GLYPH_SIZE_PX} />
      </Box>
    </ButtonBase>
  );
}

export default MinimizedBar;
